import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Activity, AlertTriangle, CheckCircle, Clock, XCircle, Search } from 'lucide-react';

const logsData = [
  {
    timestamp: '2025-10-24 14:32:15',
    client: 'ABC Trading LLC',
    erp: 'SAP',
    endpoint: '/api/v2/process',
    type: 'Sales Invoice',
    status: 'success',
    latency: '245ms',
    ftaStatus: 'Forwarded',
  },
  {
    timestamp: '2025-10-24 14:31:48',
    client: 'XYZ Corporation',
    erp: 'Oracle',
    endpoint: '/api/v2/process',
    type: 'Purchase Invoice',
    status: 'success',
    latency: '312ms',
    ftaStatus: 'Forwarded',
  },
  {
    timestamp: '2025-10-24 14:30:22',
    client: 'Global Enterprises',
    erp: 'Tally',
    endpoint: '/api/v2/process',
    type: 'Credit Note',
    status: 'failed',
    latency: '189ms',
    ftaStatus: 'Failed',
  },
  {
    timestamp: '2025-10-24 14:29:55',
    client: 'Tech Solutions FZ',
    erp: 'Zoho',
    endpoint: '/api/v2/process',
    type: 'Sales Invoice',
    status: 'success',
    latency: '278ms',
    ftaStatus: 'Forwarded',
  },
  {
    timestamp: '2025-10-24 14:28:33',
    client: 'Innovation Labs',
    erp: 'Dynamics',
    endpoint: '/api/v2/process',
    type: 'Purchase Invoice',
    status: 'pending',
    latency: '456ms',
    ftaStatus: 'Pending',
  },
  {
    timestamp: '2025-10-24 14:27:12',
    client: 'ABC Trading LLC',
    erp: 'SAP',
    endpoint: '/api/v2/process',
    type: 'Debit Note',
    status: 'success',
    latency: '223ms',
    ftaStatus: 'Forwarded',
  },
];

export default function SuperAdminLogs() {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredLogs = logsData.filter(log => {
    const matchesSearch = log.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || log.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl text-gray-900">Logs & Monitoring</h1>
        <p className="text-gray-600 mt-1">Real-time API monitoring and audit logs</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total API Calls</p>
                <p className="text-2xl text-gray-900 mt-2">2.4M</p>
                <p className="text-sm text-gray-500 mt-1">Today</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-[#0052CC]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Successful</p>
                <p className="text-2xl text-gray-900 mt-2">2.35M</p>
                <p className="text-sm text-green-600 mt-1">98.2%</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl text-gray-900 mt-2">32K</p>
                <p className="text-sm text-red-600 mt-1">1.3%</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Latency</p>
                <p className="text-2xl text-gray-900 mt-2">267ms</p>
                <p className="text-sm text-gray-500 mt-1">Response time</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Alerts */}
      <Card>
        <CardHeader>
          <CardTitle>System Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <div className="flex-1">
                <p className="text-sm text-gray-900">High API usage detected for XYZ Corporation</p>
                <p className="text-xs text-gray-600">Approaching rate limit threshold - 12 mins ago</p>
              </div>
              <Badge variant="outline" className="bg-yellow-100">Warning</Badge>
            </div>
            <div className="flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div className="flex-1">
                <p className="text-sm text-gray-900">FTA Peppol connection restored</p>
                <p className="text-xs text-gray-600">All systems operational - 1 hour ago</p>
              </div>
              <Badge variant="outline" className="bg-green-100">Resolved</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                placeholder="Search by client or transaction type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent API Logs ({filteredLogs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>ERP</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Latency</TableHead>
                <TableHead>FTA Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log, index) => (
                <TableRow key={index}>
                  <TableCell className="text-sm text-gray-600">{log.timestamp}</TableCell>
                  <TableCell>{log.client}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{log.erp}</Badge>
                  </TableCell>
                  <TableCell>{log.type}</TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(log.status)}
                      <span className="capitalize">{log.status}</span>
                    </div>
                  </TableCell>
                  <TableCell>{log.latency}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        log.ftaStatus === 'Forwarded' ? 'default' :
                        log.ftaStatus === 'Failed' ? 'destructive' :
                        'secondary'
                      }
                    >
                      {log.ftaStatus}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <button className="text-[#0052CC] hover:underline text-sm">
                      View Details
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
